import React from 'react';
import { identifyComponent } from '../../util';
import { Flex } from '../Flex';
import { HeightOptions } from '../MultiSelectMenuOptions/MultiSelectMenuOptions';
import { NestedDragAndDropMenu } from '../NestedDragAndDropMenu';
import { SelectProps } from '../Select';
import { MultiSelectMenuProps, useMultiSelectMenu } from './useMultiSelectMenu';

interface MultiSelectMenuComponentProps<T> extends MultiSelectMenuProps<T> {
  /**
   * If true, a controlled TextInput will appear at the top of the list to filter the options
   * by their label.
   */
  filterable?: boolean;

  /**
   * If provided, sets max height and hides the overflow
   */
  menuHeight?: HeightOptions;

  /**
   * If provided, sets max height just on the options
   */
  menuOptionsHeight?: HeightOptions;

  /**
   * If provided, sets max width for the select menu content
   */
  menuWidth?: SelectProps['contentWidth'];

  /**
   * Allows for sorting of options.
   * Current just supports sorting hoisted options.
   * Must inject Muuri dependency.
   */
  sortable?: {
    behavior: 'hoisted';
    Muuri: React.ComponentProps<typeof NestedDragAndDropMenu>['Muuri'];
  };
}

export function MultiSelectMenu<T>({
  filterable = false,
  menuWidth,
  menuHeight,
  menuOptionsHeight,
  testId = 'multi-select-menu',
  sortable,
  ...multiSelectMenuProps
}: MultiSelectMenuComponentProps<T>) {
  const {
    Select,
    selectProps,
    Filter,
    filterProps,
    filterRef,
    Options,
    optionsProps,
    Footer,
    footerProps,
  } = useMultiSelectMenu({
    testId,
    ...multiSelectMenuProps,
  });

  return (
    <Select
      data-component="MultiSelectMenu"
      onFirstUpdate={() => filterRef.current?.focus()}
      contentWidth={menuWidth}
      // Set the min-width to 260px to prevent wrapping in other languages (like German)
      minWidth={260}
      {...selectProps}
      {...identifyComponent('multi-select-menu', selectProps)}
    >
      <Flex column alignItems="stretch" style={{ maxHeight: menuHeight }}>
        {filterable && <Filter ref={filterRef} {...filterProps} />}
        <Options
          {...optionsProps}
          maxHeight={menuOptionsHeight}
          sortable={sortable}
          menuWidth={menuWidth}
        />
        <Footer {...footerProps} />
      </Flex>
    </Select>
  );
}
